import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from './utils';

export type SliderProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label?: ReactNode;
  /** Renders the current value to the right of the label. */
  showValue?: boolean;
  /** Suffix appended to the rendered value (e.g. "px", "%"). */
  unit?: string;
};

/**
 * Slider — styled `<input type="range">` with a gold-tinted track fill.
 * Track fill scales with `value` between `min` and `max`.
 */
export const Slider = forwardRef<HTMLInputElement, SliderProps>(function Slider(
  {
    label,
    showValue,
    unit,
    className,
    id,
    min = 0,
    max = 100,
    value,
    defaultValue,
    ...rest
  },
  ref,
) {
  const reactId = useId();
  const fieldId = id ?? `slider-${reactId}`;
  const v = Number(value ?? defaultValue ?? 0);
  const pct = Math.max(
    0,
    Math.min(100, ((v - Number(min)) / (Number(max) - Number(min))) * 100),
  );
  return (
    <div className={cn('w-full', className)}>
      {(label || showValue) && (
        <div className="flex items-center justify-between mb-2">
          {label && (
            <label htmlFor={fieldId} className="text-[12px] font-medium text-ink-700">
              {label}
            </label>
          )}
          {showValue && (
            <span className="text-[11.5px] text-ink-500 tabular">
              {v}
              {unit ?? ''}
            </span>
          )}
        </div>
      )}
      <input
        ref={ref}
        id={fieldId}
        type="range"
        min={min}
        max={max}
        value={value}
        defaultValue={defaultValue}
        {...rest}
        className="w-full h-1.5 rounded-full appearance-none focus-ring cursor-pointer"
        style={{
          background: `linear-gradient(to right, var(--color-gold-500) 0%, var(--color-gold-500) ${pct}%, var(--color-ink-100) ${pct}%, var(--color-ink-100) 100%)`,
        }}
      />
    </div>
  );
});
